import { useEffect } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { useLoaderData, useLocation, useRouteError, useRouteLoaderData } from 'react-router';
import FingerprintSDK from '@cx-sdk/fp';
import RootErrorPage, { DEFAULT_RESOURCES_URL, extractException } from '@cx-web/components/composites/RootErrorPage';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { LegacyAuthModalBridge } from '@cx-web/feature-auth-ui/legacy-bridge';
import { AuthModalProvider } from '@cx-web/feature-auth-ui/provider';
import { BetslipProvider } from '@cx-web/feature-betslip/context';
import { FeatureFlagProvider, featureFlags } from '@cx-web/feature-flags/provider';
import { GeolocationProvider } from '@cx-web/feature-geolocation/context';
import { LabelProvider } from '@cx-web/feature-i18n';
import { MyOCAuthProvider, useMyOCLoginInit } from '@cx-web/feature-myoc';
import { RepubProvider } from '@cx-web/feature-repub';
import { SiteConfigProvider } from '@cx-web/feature-site-config';
import { getRepubFromURL, isErrorWithStatus, updateI18nSingleton } from '@cx-web/utils';
import { captureException } from '@sentry/react-router';
import { createInstance } from 'i18next';

import { LegacyModalProvider } from '~/components/composites/legacy-auth-modal';
import { type loader as rootLoader } from '~/root-loader';

import Body from './body';
import Head from './head';
import { type loader } from './loader.server';

// Re-export loader directly from loader.server, meta and handle from implementation
export { loader } from './loader.server';
export { meta, handle } from './route-impl';

/**
 * Shared root error boundary. It must never fail: it reads only the lightweight
 * `'root'` loader (repub from the OC-Repub header, resourcesUrl from env) and falls
 * back to the URL-derived repub and the CDN default when that data is absent — which
 * is exactly when an API caused the error. RootErrorPage renders the whole document
 * from those inputs alone.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  captureException(extractException(error));

  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  const location = useLocation();
  const repub = rootLoaderData?.repub ?? getRepubFromURL(new URL(`https://www.oddschecker.com${location.pathname}`));
  const errorStatus = isErrorWithStatus(error) ? error.status : null;

  return (
    <RootErrorPage
      errorStatus={errorStatus}
      repub={repub}
      resourcesUrl={rootLoaderData?.resourcesUrl ?? DEFAULT_RESOURCES_URL}
    />
  );
}

const i18n = createInstance();
i18n.use(initReactI18next);

function PageTemplate() {
  const rootLoaderData = useLoaderData<typeof loader>();
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    ENV,
    featureToggleDefinitions,
    geolocationInfo,
    labels,
    lng,
    myocConfig,
    myocData,
    oddsFormat,
    persistedBetslipData,
    repub,
    resources,
    siteConfig,
  } = rootLoaderData;

  updateI18nSingleton(i18n, lng, resources);

  useMyOCLoginInit(myocConfig);

  useEffect(() => {
    const fingerprintingEnabled = featureToggleDefinitions.find(
      def => def.name === featureFlags.SUBS__FINGERPRINTING,
    )?.enabled;
    if (!fingerprintingEnabled) {
      return;
    }

    async function fingerprintClient() {
      if (localStorage && !localStorage.getItem('fpsdk-set')) {
        try {
          const fingerprintSDK = await FingerprintSDK.load();
          const result = await fingerprintSDK.get();
          const response = await fetch('/api/cx/fp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(result),
          });
          const payload = (await response.json()) as { visitorId: string };
          if (payload.visitorId) {
            localStorage.setItem('fpsdk-set', payload.visitorId);
          }
        } catch (error) {}
      }
    }
    fingerprintClient();
  }, [featureToggleDefinitions]);

  return (
    <I18nextProvider i18n={i18n}>
      <html lang={lng}>
        <FeatureFlagProvider featureToggleDefinitions={featureToggleDefinitions}>
          <RepubProvider repub={repub}>
            <MyOCAuthProvider myocConfig={myocConfig} myocData={myocData as MyOCData}>
              <LegacyModalProvider>
                <GeolocationProvider countryCode={geolocationInfo.countryCode} state={geolocationInfo.subdivisionCode}>
                  <SiteConfigProvider siteConfig={siteConfig}>
                    <LabelProvider devEnv={ENV.NODE_ENV !== 'production'} labels={labels}>
                      <BetslipProvider
                        bookmakers={siteConfig.bookmakers}
                        countryCode={geolocationInfo.countryCode}
                        persistedBetslipData={persistedBetslipData}
                        repub={repub}
                        subdivisionCode={geolocationInfo.subdivisionCode}
                        userOddsFormat={oddsFormat}
                      >
                        {/* Framework-agnostic auth-modal seam (NGF-6331). The bridge drives the
                            legacy OC widget from the store so consumers stop touching window.ocAuth. */}
                        <AuthModalProvider>
                          <Head />
                          <Body />
                          <LegacyAuthModalBridge />
                        </AuthModalProvider>
                      </BetslipProvider>
                    </LabelProvider>
                  </SiteConfigProvider>
                </GeolocationProvider>
              </LegacyModalProvider>
            </MyOCAuthProvider>
          </RepubProvider>
        </FeatureFlagProvider>
      </html>
    </I18nextProvider>
  );
}

export default PageTemplate;
